









































import Vue from 'vue';
import { mapActions } from 'vuex';
import gql from 'graphql-tag';

import { omit } from 'lodash';

import { BButton } from 'bootstrap-vue';
import LoadingSpinner from '@reciprocity/loading-spinner';
import { keysToSnake, keysToCamel } from '@/utils';

import { TaskTemplate, taskErrorType } from './types';
import RiskTaskTemplate from './components/RiskTaskTemplate.vue';

const UPDATE_RISK_TASK_TEMPLATE = gql`
  mutation UpdateTask($id: ID!, $input: UpdateObjectTemplateInput!) {
    result: updateObjectTemplate(id: $id, input: $input) {
      template: result {
        id
        content {
          value
        }
      }
    }
  }
`;

export default Vue.extend({
  name: 'TasksTab',
  components: {
    BButton,
    RiskTaskTemplate,
    LoadingSpinner,
  },
  data: () => ({
    userErrorShowed: <boolean>false,
    people: [],
    templates: <TaskTemplate[]>[],
  }),
  apollo: {
    people: {
      query: gql`
        query {
          search(type: Person) {
            edges {
              node {
                id
                ... on Person {
                  dbId
                  name {
                    value
                  }
                  email {
                    value
                  }
                  userRole {
                    value
                  }
                }
              }
            }
          }
        }
      `,
      update: result => {
        return result.search.edges.map((o: any) => ({
          id: o.node.dbId,
          label: o.node.name.value,
          value: o.node.email.value,
          role: o.node.userRole.value,
        }));
      },
      error(error) {
        this.showLoadError(error.toString());
      },
    },
    templates: {
      query: gql`
        query objectTemplate {
          search(
            type: ObjectTemplate
            filter: "{\\"left\\":\\"source_type\\",\\"op\\":{\\"name\\":\\"=\\"}, \\"right\\":\\"Risk\\"}"
          ) {
            edges {
              node {
                id
                ... on ObjectTemplate {
                  id
                  active {
                    value
                  }
                  actionTrigger {
                    value
                  }
                  content {
                    value
                  }
                  objectType {
                    value
                  }
                }
              }
            }
          }
        }
      `,
      update: data => {
        if (!data.search) return data;
        const templates = data.search.edges.map((e: any) => ({
          id: e.node.id,
          active: e.node.active.value,
          actionTrigger: e.node.actionTrigger.value,
          ...keysToCamel(JSON.parse(e.node.content.value)),
        }));
        return templates;
      },
      error(error) {
        this.showLoadError(error.toString());
      },
    },
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),
    onTemplateChange(val: TaskTemplate) {
      let index = this.templates.findIndex(o => o.id === val.id);
      if (!~index) return;
      this.templates[index] = val;
    },
    validate(): String[] {
      let errored = (this.$refs['risk-task-template'] as Array<any>).filter(o => !o.validate());
      if (errored.length) {
        this.scrollToTemplate(errored[0].template.id);
      }
      return errored.map(o => o.error);
    },
    scrollToTemplate(templateId: string) {
      let ref = (this.$refs['risk-task-template'] as Array<any>).find(o => o.template.id === templateId);
      if (!ref) return;
      ref.$el.scrollIntoView({ behavior: 'smooth' });
    },
    showLoadError(): void {
      // Ugly fix to not presenting 4 messages for same error:
      // todo: Find out why error hook is called four times (yet only one actual request is sent. Caching may be a problem, but even with fetchPolicy: 'no-cache' hook is called 2x).
      if (this.userErrorShowed) return;
      this.userErrorShowed = true;
      this.showAlert({
        type: 'error',
        message: 'Failed to load task templates data.',
      });
    },
    async saveTemplate(template: TaskTemplate): Promise<taskErrorType> {
      // This sends gql request for each template, but fortunatelly
      // gql is smart enough to combine them into single actual request.
      // Returns true if error occured
      try {
        const ignored = ['id', 'active', 'actionTrigger'];
        const variables = {
          id: template.id,
          input: {
            active: template.active,
            content: JSON.stringify(keysToSnake(omit(template, ignored))),
            actionTrigger: template.actionTrigger,
            objectType: 'Task',
          },
        };

        await this.$apollo.mutate({
          mutation: UPDATE_RISK_TASK_TEMPLATE,
          variables,
        });

        return { templateId: template.id, error: '' };
      } catch (error) {
        return { templateId: template.id, error: error };
      }
    },
    async save() {
      let cards = this.$refs['risk-task-template'] as Array<any>;

      // Clear errors:
      cards.forEach(o => o.invalidateCard(false));

      let errors = this.validate();
      if (errors.length) return;

      let actions: Promise<taskErrorType>[] = [];
      this.templates.forEach(template => {
        actions.push(this.saveTemplate(template));
      });

      // Wait for all requests to return:
      Promise.all(actions).then(results => {
        let errored = cards.filter(o => (results.find(a => a.templateId === o.template.id) || {}).error);

        if (errored.length) {
          // Set error status to each card:
          errored.forEach(o => o.invalidateCard(true));
          this.scrollToTemplate(errored[0].template.id);

          let subject = errored.length > 1 ? 'some templates' : 'a template';
          this.showAlert({
            type: 'error',
            message: `Failed to save ${subject}`,
          });
        } else {
          this.showAlert({
            type: 'success',
            message: 'Templates updated sucessfuly',
          });
        }
      });
    },
  },
});
